import { useParams } from "react-router-dom";
import { CheckCircle2, MapPin, MessageCircle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { StructuredData } from "@/components/StructuredData";
import { Navbar } from "@/components/Navbar";
import { ServicesSection } from "@/components/ServicesSection";
import { ResidentialSection } from "@/components/ResidentialSection";
import { ProcessSection } from "@/components/ProcessSection";
import { LeadForm } from "@/components/LeadForm";
import { FinalCTA } from "@/components/FinalCTA";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Reveal } from "@/components/Reveal";
import { WhatsAppButton, QuoteButton } from "@/components/CtaButtons";
import { business } from "@/config/business";
import { seoPages } from "@/data/seoPages";

/**
 * SeoLanding — layout halaman SEO/Google Ads yang dapat dipakai ulang.
 * Konten unik diambil dari src/data/seoPages.js berdasarkan slug.
 */
export default function SeoLanding({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = seoPages[slug];

  if (!page) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F172A] text-white">
        Halaman tidak ditemukan.
      </div>
    );
  }

  const waMsg =
    page.audience === "b2c"
      ? business.whatsappMessages.b2c
      : business.whatsappMessages.b2b;
  const primaryLabel = page.primaryCtaLabel || business.cta.primary;
  const secondaryLabel = page.secondaryLabel || business.cta.secondary;

  return (
    <>
      <Seo
        title={page.seo.title}
        description={page.seo.description}
        keywords={page.seo.keywords}
        canonical={`/${page.slug}`}
      />
      <StructuredData />
      <Navbar />

      {/* Hero */}
      <section
        data-testid="seo-hero"
        className="relative flex min-h-[88svh] items-center overflow-hidden bg-[#0F172A]"
      >
        <div className="absolute inset-0 z-0">
          <img src={page.heroImage} alt={page.heroImageAlt} className="h-full w-full object-cover" loading="eager" />
        </div>
        <div className="absolute inset-0 z-[1] bg-[#0F172A]/72" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0F172A] via-[#0F172A]/60 to-[#0F172A]/20" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pt-32">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5">
              <span className="flex h-2 w-2 rounded-full bg-[#25D366]" />
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
                {page.kicker} · {business.city}
              </span>
            </div>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {page.h1}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {page.intro}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <QuoteButton label={primaryLabel} source={`seo_${page.slug}_hero`} size="lg" variant="accent" testId="seo-hero-quote" className="w-full !bg-[#25D366] !text-white hover:!bg-[#1eb85a] sm:w-auto" />
              <WhatsAppButton label={secondaryLabel} message={waMsg} source={`seo_${page.slug}_hero`} size="lg" testId="seo-hero-whatsapp" className="w-full !bg-transparent !text-white ring-1 ring-inset ring-white/25 hover:!bg-white/10 sm:w-auto" />
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-6 text-sm text-slate-300">
              <span className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#25D366]" />WhatsApp {business.whatsapp.display}</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#25D366]" />{business.serviceArea}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-20 sm:py-24" data-testid="seo-benefits">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-16">
            <div>
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">Manfaat</span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                {page.benefitsTitle}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                Layanan diarahkan untuk kebutuhan perusahaan dan fasilitas komersial,
                dengan pendekatan perawatan berkala yang terencana.
              </p>
            </div>
            <ul className="space-y-4">
              {page.benefits.map((b, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="flex items-start gap-3 rounded-xl border border-slate-200 bg-[#F8FAFC] p-5">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#25D366]" />
                    <span className="text-[15px] leading-relaxed text-slate-700">{b}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {page.showResidential ? <ResidentialSection /> : <ServicesSection />}
      {page.audience !== "b2c" && <ProcessSection />}
      <LeadForm />

      {/* Page-specific FAQ */}
      <section id="faq" className="bg-white py-20 sm:py-24" data-testid="seo-faq">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">FAQ</span>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Pertanyaan Seputar {page.kicker}
          </h2>
          <Accordion type="single" collapsible className="mt-8 w-full">
            {page.faqs.map((faq) => (
              <AccordionItem key={faq.id} value={faq.id} className="border-slate-200" data-testid={`seo-faq-${faq.id}`}>
                <AccordionTrigger className="py-5 text-left text-base font-bold text-slate-900 hover:no-underline sm:text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <FinalCTA />
      <Footer />
      <WhatsAppFloat />
    </>
  );
}
